import * as Commons from "oc-commons/Commons";

/**
 * Interface für das LocalSettings Handler Objekt
 */
export interface ILocalSettingsHandler<TSettings = Record<string, unknown>> {
  /** Lesezugriff auf die im LocalStorage gespeicherten Settings */
  get: () => Partial<TSettings>;
  /** Lesezugriff mit Merge der Default-Settings */
  getMerge: () => TSettings;
  /** Löscht die Settings aus dem LocalStorage */
  remove: () => void;
  /** Speichert die Settings im LocalStorage */
  set: (config: TSettings | null) => string | null;
  /** Speichert die Settings mit Merge der bestehenden Settings */
  setMerge: (config: Partial<TSettings>) => string | null;
}

/**
 * Erlaubt es, Änderungen in den Settings lokal im Browser zu speichern
 * @param localStorageKey der App-Name
 * @param defaultSettings die normalen Settings der App
 * @returns Handler-Objekt mit get, getMerge, remove, set, setMerge Methoden
 * @example
 * const settingsHandler = localSettings('zaunplanerAppSettings',settings);
 * const app = new Zaunplaner(container, settingsHandler.getMerge());
 */
export function localSettings<TSettings = Record<string, unknown>>(
  localStorageKey: string,
  defaultSettings?: TSettings
): ILocalSettingsHandler<TSettings> {
  const get = (): Partial<TSettings> => {
    const stored = localStorage[localStorageKey];
    return JSON.parse(stored || "{}") as Partial<TSettings>;
  };

  const getMerge = (): TSettings => {
    if (defaultSettings) {
      return Commons.defaultsDeepSafe({}, get(), defaultSettings) as TSettings;
    }
    return get() as TSettings;
  };

  const remove = (): void => {
    delete localStorage[localStorageKey];
  };

  const set = (config: TSettings | null): string | null => {
    const value = config == null ? null : JSON.stringify(config);
    localStorage[localStorageKey] = value as string;
    return value;
  };

  const setMerge = (config: Partial<TSettings>): string | null => {
    const merged = Commons.defaultsDeepSafe({}, config, get()) as TSettings;
    return set(merged);
  };

  return {
    get,
    getMerge,
    remove,
    set,
    setMerge,
  };
}
